import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { showAlert } from 'components/shared/Alert/Alert';
import { usePaymentContext } from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';
import { chargeElavonOnlineHandler } from 'components/Payments/NewPayment/PaymentContent/utils/elavonMethods';

import { buildBillingAddress } from './preAuthFormHelpers';
import { PreAuthModalValidationSchema } from './validation';

// Elavon collects the card in a Converge lightbox, so the card is tokenized before the
// signature is taken; the resulting card on file is what the authorization is recorded against.
export const useElavonTokenize = ({ formik, patientId, onTokenized }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { terminal } = usePaymentContext();
  const [isTokenizing, setIsTokenizing] = useState(false);

  const tokenize = async () => {
    if (isTokenizing) return;

    try {
      await PreAuthModalValidationSchema.validateAt('cardholderName', formik.values);
    } catch (error) {
      formik.setFieldTouched('cardholderName', true, false);
      showAlert({
        title: 'Cardholder name is required',
        message: error?.message,
        color: 'danger'
      });
      return;
    }

    setIsTokenizing(true);
    try {
      const response = await chargeElavonOnlineHandler(navigate, {
        amount: 0,
        tokenOnly: true,
        saveCard: true,
        patientId,
        terminalId: terminal?.id,
        billingAddress: buildBillingAddress(formik.values)
      });

      const cardId = response?.card?.id || response?.cardId;
      if (response?.code !== 0 || !cardId) {
        throw new Error(response?.error || "Couldn't save the card");
      }

      await queryClient.refetchQueries(['cards']);
      onTokenized(cardId);
    } catch (error) {
      showAlert({
        title: "Couldn't add the card",
        message: error?.message || 'Please try again or contact support.',
        color: 'danger'
      });
    } finally {
      setIsTokenizing(false);
    }
  };

  return { tokenize, isTokenizing };
};
